<template>
    <canvas
        ref="canvas"
        class="code-matrix"
        :style="{ opacity: codeScreenOpacity }"
        aria-hidden="true" />
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { backgroundCode } from '@/data/backgroundCode';
import { codeScreenInSight, codeScreenOpacity } from '@/composables/useCodeScreenSight';
import { tokenize } from '@/utils/highlight';
import { HORIZON } from './NeuralWaveBackground.vue';

const FONT_FAMILY = "ui-monospace, 'Cascadia Mono', 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";
const TOP = 80; // px from the top of the viewport to the first line, clear of the app bar
const MARGIN = 0.04; // left margin, as a fraction of the viewport width
const OPACITY = 0.65; // of the whole screen, so it stays as dim as the wave
const FULL_LINES = 0.4; // share of the newest lines drawn at full opacity, the older ones fade out
const TYPE_DELAY = 10; // ms per character, randomised
const LINE_DELAY = 100; // ms after each line break
const SNIPPET_DELAY = 1800; // ms before typing the next snippet
const TRAIL = 200; // ms a typed character keeps glowing
const BLINK = 530; // ms per caret blink phase
const SCROLL_EASE = 0.08; // s, time constant of the scroll easing
const PARALLAX = 12; // px the screen slides following the pointer, against the wave's camera
const REFRESH_PERIOD = 7; // s for the refresh bar to sweep down the screen
const REFRESH_HEIGHT = 140; // px
const SCANLINE_OPACITY = 0.35;
const GLITCH_PAUSE = [200, 1000]; // ms between two glitches, randomised in this range
const ADDRESS_BASE = 0x7f3a00;
const ADDRESS_OPACITY = 0.45;

// [theme color, opacity] of each token kind of the highlighter, `plain` for the text between tokens.
const TONES = {
    plain: ['network', 1],
    comment: ['network', 0.55],
    string: ['glow', 0.4],
    decorator: ['glow', 0.4],
    declaration: ['secondary', 0.4],
    keyword: ['secondary', 0.4],
    literal: ['glow', 0.55],
    type: ['secondary', 0.3],
    call: ['secondary', 0.3],
    operator: ['network', 0.8],
    bracket: ['network', 0.8],
};

/**
 * Every snippet split into lines, each holding its runs of same-kind text and the delay before
 * it gets typed. A blank line closes each snippet. Whitespace isn't drawn, so it has no runs.
 */
const SCRIPT = backgroundCode.flatMap((code) => {
    const lines = [{ text: '', runs: [], delay: SNIPPET_DELAY }];
    for (const [text, kind] of tokenize(code)) {
        text.split('\n').forEach((part, i) => {
            if (i > 0) lines.push({ text: '', runs: [], delay: LINE_DELAY });
            const line = lines[lines.length - 1];
            if (part.trim()) line.runs.push({ text: part, tone: TONES[kind ?? 'plain'], column: line.text.length });
            line.text += part;
        });
    }
    lines.push({ text: '', runs: [], delay: LINE_DELAY });
    return lines.map((line) => ({ ...line, indent: line.text.length - line.text.trimStart().length }));
});

const smoothstep = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
const between = ([min, max]) => min + Math.random() * (max - min);
/** `color` (#rrggbb or #rrggbbaa), fully transparent: fades a gradient without going through black. */
const transparent = (color) => `${color.slice(0, 7)}00`;

/**
 * Background code screen, behind the neural wave: endless code typed line after line from
 * the top of the viewport. Once the caret reaches the wave's horizon, every new line scrolls
 * the screen up, the oldest lines fading out as they climb.
 */
export default {
    name: 'CodeMatrixBackground',
    setup() {
        const canvas = ref(null);
        const { $vuetify } = getCurrentInstance().proxy;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let ctx = null;
        let theme = null; // colors of the frame being drawn
        let width = 0;
        let height = 0;
        let lineHeight = 0;
        let charWidth = 0;
        let rows = 1; // lines fitting between the top and the horizon
        let scanlines = null;
        let frame = 0;
        let last = 0;

        let lines = []; // on screen, oldest first: { source: SCRIPT line, typed: chars, stamps: typing times }
        let first = 0; // number of lines typed before lines[0], so the line index of lines[0]
        let cursor = -1; // SCRIPT index of the newest line
        let nextKey = 0; // time of the next keystroke
        let lastKey = 0; // time of the last keystroke, the caret stays solid while typing
        let scroll = 0;
        let targetScroll = 0;
        let offset = 0;
        let targetOffset = 0;
        let glitch = { start: 0, end: 0, from: 0, to: 0, shift: 0 };

        const caretLine = () => first + lines.length - 1;
        // Scrolls whole lines, just enough to keep the caret line above the horizon.
        const fitCaret = () => Math.max(0, (caretLine() + 1 - rows) * lineHeight);

        /** Types the next character, or breaks the line. Returns the delay until the next keystroke. */
        function keystroke(now) {
            const line = lines[lines.length - 1];
            if (line && line.typed < line.source.text.length) {
                line.stamps[line.typed++] = lastKey = now;
                return TYPE_DELAY * (0.25 + Math.random());
            }
            cursor = (cursor + 1) % SCRIPT.length;
            // Starts past the indentation, like an editor's auto-indent.
            lines.push({ source: SCRIPT[cursor], typed: SCRIPT[cursor].indent, stamps: [] });
            targetScroll = fitCaret();
            return SCRIPT[cursor].delay;
        }

        /** Drops the lines that have faded out and scrolled away. */
        function trim() {
            while (caretLine() - first > rows + 1) {
                lines.shift();
                first++;
            }
        }

        /** Fills the screen at once, for a still frame. */
        function fill() {
            lines = SCRIPT.slice(0, rows).map((source) => ({ source, typed: source.text.length, stamps: [] }));
            first = 0;
            cursor = lines.length - 1;
            scroll = targetScroll = 0;
        }

        /** Opacity of the n-th line: full for the newest ones, fading out up to the top of the screen. */
        function lineOpacity(n) {
            const fullRows = rows * FULL_LINES;
            return OPACITY * (1 - smoothstep((caretLine() - n - fullRows) / (rows - fullRows)));
        }

        /** Draws the typed part of `line`, in its tones or all in `color`. */
        function drawLine(line, x, y, opacity, color) {
            for (const { text, tone, column } of line.source.runs) {
                if (column >= line.typed) break;
                ctx.fillStyle = color || theme[tone[0]];
                ctx.globalAlpha = opacity * tone[1];
                ctx.fillText(text.slice(0, line.typed - column), x + column * charWidth, y);
            }
        }

        function draw(now) {
            theme = $vuetify.theme.currentTheme;
            const left = Math.round(width * MARGIN) + offset;
            const codeX = left + charWidth * 10; // after the 8-character address and 2 spaces
            const bottom = height * HORIZON;
            const glitching = now >= glitch.start && now < glitch.end;

            ctx.globalCompositeOperation = 'source-over';
            ctx.shadowBlur = 0;
            ctx.clearRect(0, 0, width, height);

            // Gutter rule, fading out towards the top like the lines.
            const rule = ctx.createLinearGradient(0, 0, 0, bottom);
            rule.addColorStop(0, transparent(theme.network));
            rule.addColorStop(1, theme.network);
            ctx.globalAlpha = OPACITY * 0.5;
            ctx.fillStyle = rule;
            ctx.fillRect(codeX - charWidth, 0, 1, bottom);

            lines.forEach((line, i) => {
                const n = first + i;
                const y = TOP + (n + 0.5) * lineHeight - scroll;
                const opacity = lineOpacity(n);
                if (opacity <= 0 || y < -lineHeight || y > height + lineHeight) return;

                ctx.globalAlpha = opacity * ADDRESS_OPACITY;
                ctx.fillStyle = theme.network;
                ctx.fillText(`0x${(ADDRESS_BASE + n * 0x10).toString(16).toUpperCase()}`, left, y);

                if (glitching && n >= glitch.from && n <= glitch.to) {
                    // Torn line: shifted, with cyan and magenta ghosts on both sides.
                    drawLine(line, codeX + glitch.shift - 3, y, opacity * 0.6, '#00ffff');
                    drawLine(line, codeX + glitch.shift + 3, y, opacity * 0.6, '#ff00ff');
                    drawLine(line, codeX + glitch.shift, y, opacity);
                } else {
                    drawLine(line, codeX, y, opacity);
                }
            });

            const line = lines[lines.length - 1];
            if (line) {
                const y = TOP + (caretLine() + 0.5) * lineHeight - scroll;

                // Current line highlight.
                ctx.globalAlpha = OPACITY * 0.06;
                ctx.fillStyle = theme.glow;
                ctx.fillRect(codeX - charWidth, y - lineHeight / 2, width, lineHeight);

                // The last typed characters glow, then cool down to their color.
                ctx.fillStyle = ctx.shadowColor = theme.glow;
                ctx.shadowBlur = 8;
                for (const recent of lines.slice(-2)) {
                    const lineY = TOP + (first + lines.indexOf(recent) + 0.5) * lineHeight - scroll;
                    for (let k = recent.typed - 1; k >= 0 && now - recent.stamps[k] < TRAIL; k--) {
                        ctx.globalAlpha = OPACITY * 1.6 * (1 - (now - recent.stamps[k]) / TRAIL) ** 2;
                        ctx.fillText(recent.source.text[k], codeX + k * charWidth, lineY);
                    }
                }

                // Block caret, solid while typing and blinking while waiting.
                if (now - lastKey < BLINK || Math.floor(now / BLINK) % 2 === 0) {
                    ctx.globalAlpha = OPACITY * 1.2;
                    ctx.fillRect(codeX + line.typed * charWidth, y - lineHeight / 2 + 2, charWidth, lineHeight - 4);
                }
                ctx.shadowBlur = 0;
            }

            // Refresh bar: a band of light sweeping down, tinting the text it crosses.
            const barY = (((now / 1000) % REFRESH_PERIOD) / REFRESH_PERIOD) * (bottom + REFRESH_HEIGHT) - REFRESH_HEIGHT;
            const bar = ctx.createLinearGradient(0, barY, 0, barY + REFRESH_HEIGHT);
            bar.addColorStop(0, transparent(theme.glow));
            bar.addColorStop(0.85, theme.glow);
            bar.addColorStop(1, transparent(theme.glow));
            ctx.globalCompositeOperation = 'source-atop';
            ctx.globalAlpha = 0.7;
            ctx.fillStyle = bar;
            ctx.fillRect(0, barY, width, REFRESH_HEIGHT);

            // CRT scanlines, carved out of the text.
            ctx.globalCompositeOperation = 'destination-out';
            ctx.globalAlpha = SCANLINE_OPACITY;
            ctx.fillStyle = scanlines;
            ctx.fillRect(0, 0, width, height);
        }

        /** Tears a few random visible lines apart for a moment, now and then. */
        function scheduleGlitch(now) {
            const from = caretLine() - Math.floor(Math.random() * rows * FULL_LINES * 1.5);
            glitch = {
                start: now + between(GLITCH_PAUSE),
                from,
                to: from + Math.floor(Math.random() * 3),
                shift: (Math.random() - 0.5) * 40,
            };
            glitch.end = glitch.start + between([60, 180]);
        }

        function loop(now) {
            const dt = Math.min((now - last) / 1000, 0.1);
            last = now;
            // Back from a hidden tab or a pause: resumes typing instead of catching up all at once.
            if (now - nextKey > 1000) nextKey = now;
            while (nextKey <= now) nextKey += keystroke(nextKey);
            if (now >= glitch.end) scheduleGlitch(now);

            scroll += (targetScroll - scroll) * (1 - Math.exp(-dt / SCROLL_EASE));
            offset += (targetOffset - offset) * 0.04;
            trim();
            draw(now);
            frame = requestAnimationFrame(loop);
        }

        /** Starts the animation loop again, from where it stopped. */
        function play() {
            last = performance.now();
            frame = requestAnimationFrame(loop);
        }

        /** Stops the animation loop on a still frame, without the glitch it may have stopped on. */
        function pause() {
            cancelAnimationFrame(frame);
            frame = 0;
            glitch.end = 0;
            draw(last);
        }

        function resize() {
            const el = canvas.value;
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            width = el.clientWidth;
            height = el.clientHeight;
            el.width = Math.round(width * ratio);
            el.height = Math.round(height * ratio);
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

            const fontSize = width < 600 ? 11 : 13;
            lineHeight = Math.round(fontSize * 1.45);
            ctx.font = `${fontSize}px ${FONT_FAMILY}`;
            ctx.textBaseline = 'middle';
            charWidth = ctx.measureText('0').width;
            rows = Math.max(1, Math.floor((height * HORIZON - TOP) / lineHeight));

            // Rows of 1px carved every 3px.
            const tile = document.createElement('canvas');
            tile.width = 1;
            tile.height = 3;
            tile.getContext('2d').fillRect(0, 0, 1, 1);
            scanlines = ctx.createPattern(tile, 'repeat');

            if (reducedMotion) {
                fill();
                draw(0);
            } else {
                scroll = targetScroll = fitCaret();
                // Resizing cleared the canvas: while paused, the still frame has to be repainted.
                if (!frame) draw(last);
            }
        }

        function onPointerMove(event) {
            targetOffset = -(event.clientX / width - 0.5) * PARALLAX;
        }

        // Without the animation loop (reduced motion or paused), a theme switch has to repaint the new colors itself.
        watch(
            () => ['network', 'secondary', 'glow'].map((color) => $vuetify.theme.currentTheme[color]),
            () => {
                if (!frame) draw(last);
            },
        );
        // Paused while hidden behind the page, so it doesn't slow down the animations in sight.
        if (!reducedMotion) watch(codeScreenInSight, (inSight) => (inSight ? play() : pause()));

        onMounted(() => {
            ctx = canvas.value.getContext('2d');
            resize();
            window.addEventListener('resize', resize);
            if (reducedMotion) return;
            window.addEventListener('pointermove', onPointerMove, { passive: true });
            nextKey = performance.now();
            if (codeScreenInSight.value) play();
        });
        onBeforeUnmount(() => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', onPointerMove);
        });

        return { canvas, codeScreenOpacity };
    },
};
</script>

<style scoped>
.code-matrix {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
}
</style>
